import { makeStyles } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import AdminIcon from "@material-ui/icons/SupervisorAccount";
import { ErrorBoundary } from "@sentry/react";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import { fetchAdminBrandsData, fetchAdminMarketsData, fetchAdminRegionsData, fetchAdminViewsData } from "../../actions";
import { ErrorMsg } from "../../components/AppMessages";
import { CedarShellLayout } from "../../components/Layouts";
import { TabPanel } from "../../components/Tabs";
import { TORCH_RED } from "../../constants";
import AnnotationsAdmin from "./Annotations/Annotations";
import { AdminKpiUpdate } from "./KpiUpdate/KpiUpdate";
import { AdminRequests } from "./Requests/AdminRequests";
import "../../assets/styles/pages/adminShared.scss";
import UserGroups from "./UserGroups/UserGroups";
import { UserRequests } from "./UserRequests/UserRequests";
import { Users } from "./Users/Users";
import UserViews from "./UserViews/UserViews";

const adminTabs = [
  { id: "users", label: "Users", component: <Users /> },
  // Dashboard-access requests and signup requests are different flows (grant access vs. create an
  // account) with different approve actions, so each stays its own tile - just stacked under one
  // "Requests" tab instead of two separate tabs.
  {
    id: "requests",
    label: "Requests",
    component: (
      <>
        <AdminRequests />
        <UserRequests />
      </>
    ),
  },
  { id: "user-groups", label: "Geo Scope", component: <UserGroups /> },
  { id: "user-views", label: "User Views", component: <UserViews /> },
  { id: "kpi-update", label: "KPI update log", component: <AdminKpiUpdate /> },
];

const annotationsTab = { id: "annotations", label: "Annotations", component: <AnnotationsAdmin /> };

const userTabs: Array<{ id: string; label: string; component: JSX.Element }> = [];

const userViewsList = Cookies.get("views");

userViewsList && /\badmin/.test(userViewsList) && userTabs.push(...adminTabs);

userViewsList && /annotation_admin/.test(userViewsList) && userTabs.push(annotationsTab);

const a11yProps = (index: any) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
const useStyles = makeStyles({
  tabPanel: {
    background: "transparent",
    height: "auto !important",
  },
});

const NewAdminPage = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);

  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<any>, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    Promise.all([
      dispatch(fetchAdminBrandsData()),
      dispatch(fetchAdminMarketsData()),
      dispatch(fetchAdminRegionsData()),
      dispatch(fetchAdminViewsData()),
    ]);
  }, [dispatch]);

  return (
    <CedarShellLayout>
      <ErrorBoundary fallback={<ErrorMsg />}>
        <div className="admin_page" data-test-id="admin-page">
          <div className="admin_page_header">
            <AdminIcon className="page_header_icon" />
            <div>
              <h1 className="admin_page_title">Admin</h1>
              <p className="admin_page_subtitle">Manage users, scopes, views and platform configuration.</p>
            </div>
          </div>

          <div className="admin_page_tabs_wrapper">
            <AppBar position="static" elevation={0} className="admin_page_tabs">
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="admin section tabs"
                TabIndicatorProps={{ style: { backgroundColor: TORCH_RED } }}
                data-test-id="admin-section-tabs"
                // Without this, MUI's default "standard" variant doesn't wrap or scroll when the
                // tabs overflow their container - the last tab(s) (e.g. Annotations) just become
                // unreachable with no visual indication, at a narrow-but-not-mobile width (e.g. a
                // laptop screen zoomed to 125%-150%). "auto" only shows the scroll arrows once the
                // tabs actually overflow, so nothing changes at a full-width desktop viewport.
                variant="scrollable"
                scrollButtons="auto"
              >
                {userTabs.map((tab, idx) => (
                  <Tab label={tab.label} {...a11yProps(idx)} data-test-id={`${tab.id}-tab`} key={tab.id} />
                ))}
              </Tabs>
            </AppBar>
            <div className="admin_page_tabs_divider" />
          </div>

          {userTabs.map((tab, idx) => (
            <TabPanel data-test-id={`${tab.id}-panel`} value={value} index={idx} className={classes.tabPanel} key={tab.id}>
              {tab.component}
            </TabPanel>
          ))}
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={true}
          draggable={false}
          pauseOnHover={false}
        />
      </ErrorBoundary>
    </CedarShellLayout>
  );
};

export default NewAdminPage;
